/**
 * Shared content-type labels + thumbnail icon. Used by both the inline
 * RecommendationCards and the FULL-layout PlaylistDrawer so they label
 * + fall back identically. The same set is exposed as `<ThumbnailIcon
 * ext={…} />` so callers don't have to know which SVG paths back which
 * extension.
 *
 * When a content row has a real thumbnail URL we render <img>; when
 * it's null OR loads fail, we swap to this icon. Vimeo + YouTube items
 * routinely lack stored thumbnails because neither platform exposes
 * one through the embed URL.
 */

export const TYPE_LABELS: Record<string, string> = {
  pdf: 'PDF',
  video: 'Video',
  youtube: 'YouTube',
  vimeo: 'Vimeo',
  wistia: 'Wistia',
  loom: 'Loom',
  vidyard: 'Vidyard',
  brightcove: 'Brightcove',
  image: 'Image',
  'web page': 'Web page',
  html: 'HTML',
  article: 'Article',
  tour: 'Interactive',
  library: 'Content',
};

const TYPE_ICON_PATHS: Record<string, string> = {
  pdf: '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>',
  video:
    '<polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>',
  youtube:
    '<polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>',
  vimeo:
    '<polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>',
  wistia:
    '<polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>',
  loom: '<polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>',
  vidyard:
    '<polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>',
  brightcove:
    '<polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>',
  image:
    '<rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>',
  tour: '<circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8"/>',
};
const DEFAULT_ICON_PATH =
  '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>';

export function ThumbnailIcon({
  ext,
  size = 24,
}: {
  ext: string;
  size?: number;
}) {
  const inner = TYPE_ICON_PATHS[ext] ?? DEFAULT_ICON_PATH;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      // Static markup we control — safe to inject.
      dangerouslySetInnerHTML={{ __html: inner }}
    />
  );
}

export function typeLabelFor(ext: string, fallbackContentType?: string): string {
  return (
    TYPE_LABELS[ext] ??
    (ext || (fallbackContentType ? TYPE_LABELS[fallbackContentType] : null) || 'Content')
  );
}
